<template>
  <div>
    <Modal v-model="isModalVisible" :title="$t('onboarding.micPermission.title')">
      <div class="mic-permission-state">
        <div class="mic-permission-icon" aria-hidden="true">
          <font-awesome-icon icon="microphone" />
        </div>
        <p class="mic-permission-message">
          {{
            showOpenSettings
              ? $t('onboarding.micPermission.settingsHint')
              : $t('onboarding.micPermission.message')
          }}
        </p>
      </div>
      <template #footer>
        <button
          v-if="showOpenSettings"
          type="button"
          class="btn btn-primary mic-permission-action"
          @click="openSettings"
        >
          {{ $t('onboarding.micPermission.openSettings') }}
        </button>
        <button
          type="button"
          class="btn mic-permission-action"
          :class="showOpenSettings ? 'btn-outline-secondary' : 'btn-primary'"
          @click="getMicAccess"
        >
          {{
            showOpenSettings
              ? $t('onboarding.micPermission.tryAgain')
              : $t('onboarding.micPermission.allow')
          }}
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
import Modal from './Modal.vue';
import {
  NativeSettings,
  AndroidSettings,
  IOSSettings,
} from 'capacitor-native-settings';

export default {
  name: "MicPermissionRequest",
  components: {
    Modal
  },
  emits: ['close', 'granted'],
  data() {
    return {
      isModalVisible: true,
      // 'prompt' | 'denied' | 'granted' | 'unknown' (Permissions API absent).
      permissionState: 'unknown',
    };
  },
  computed: {
    isNative() {
      return !!window.Capacitor?.isNativePlatform?.();
    },
    // On native, a denied OS prompt never re-appears, so once we know the
    // state is 'denied' the settings app is the only recovery path. We also
    // fall back to it when getUserMedia keeps failing with no Permissions API.
    showOpenSettings() {
      return this.isNative && this.permissionState === 'denied';
    },
  },
  watch: {
    isModalVisible(visible) {
      if (!visible && this.permissionState !== 'granted') {
        this.$emit('close');
      }
    },
  },
  methods: {
    // Passive check: unlike getUserMedia this never flashes the OS
    // mic-in-use indicator.
    async refreshPermissionState() {
      try {
        const status = await navigator.permissions.query({ name: 'microphone' });
        this.permissionState = status.state;
        if (status.state === 'granted') {
          this.finish();
        }
      } catch (error) {
        // Permissions API unavailable (older iOS): keep 'unknown' and rely on
        // the getMicAccess result instead.
      }
    },
    async getMicAccess() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
        this.finish();
      } catch (error) {
        console.error('Microphone access denied or there was an error:', error);
        await this.refreshPermissionState();
        // Second explicit failure on native with no Permissions API answer:
        // the prompt is not coming back, offer the settings path. Only for an
        // actual permission denial though — a missing or busy mic
        // (NotFoundError, NotReadableError) is not fixable in Settings, so
        // routing there would just mislead the user.
        const isDenial =
          error?.name === 'NotAllowedError' || error?.name === 'SecurityError';
        if (this.permissionState === 'unknown' && this.isNative && isDenial) {
          this.permissionState = 'denied';
        }
      }
    },
    openSettings() {
      NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      }).catch((error) => console.error('Failed to open settings:', error));
    },
    finish() {
      this.permissionState = 'granted';
      this.$emit('granted');
      this.isModalVisible = false;
    },
    handleVisibilityChange() {
      // Coming back from the OS settings app (mostly a web/desktop path: on
      // iOS and Android a mic-permission change restarts the app process).
      if (!document.hidden) {
        this.refreshPermissionState();
      }
    },
  },
  mounted() {
    this.refreshPermissionState();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
};
</script>

<style scoped>
/* DESIGN empty-state pattern: icon on primary-soft, concise title (Modal's
   header), one primary action. Calm and explicit rather than alarm-red. */
.mic-permission-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 8px 0 4px;
  text-align: center;
}

.mic-permission-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: var(--al-primary-soft);
  color: var(--al-primary);
  font-size: 22px;
}

.mic-permission-message {
  margin: 0;
  color: var(--al-text);
  font-size: 0.9375rem;
  line-height: 1.5;
}

.mic-permission-action {
  width: 100%;
  min-height: 48px;
}
</style>
